import { ActionParamRow } from "./aiActions.helpers.decl";

type ActionParams = Record<string, unknown> | unknown[];

type ParamEntry = {
  id: string;
  label: string;
  value: unknown;
};

function isActionParams(value: unknown): value is ActionParams {
  return typeof value === "object" && value !== null;
}

// AWSJSON reaches the client as a JSON string; anything that is not an object or array is dropped.
function decodeActionParams(value: unknown): ActionParams | undefined {
  if (typeof value !== "string") {
    return isActionParams(value) ? value : undefined;
  }
  try {
    const parsed: unknown = JSON.parse(value);
    return isActionParams(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function toParamEntries(params: ActionParams, parentId: string): ParamEntry[] {
  if (Array.isArray(params)) {
    return params.map((value, index) => ({
      id: `${parentId}[${index}]`,
      label: `#${index + 1}`,
      value,
    }));
  }
  return Object.entries(params).map(([key, value]) => ({
    id: parentId ? `${parentId}.${key}` : key,
    label: key,
    value,
  }));
}

type ToRowsArgs = {
  params: ActionParams;
  depth: number;
  parentId: string;
};

function toRows({ params, depth, parentId }: ToRowsArgs): ActionParamRow[] {
  return toParamEntries(params, parentId).flatMap(({ id, label, value }) => {
    if (value === null || value === undefined) {
      return [{ kind: "empty", id, depth, label }];
    }

    const nested = decodeActionParams(value);
    if (!nested) {
      return [{ kind: "value", id, depth, label, text: String(value) }];
    }

    const children = toRows({ params: nested, depth: depth + 1, parentId: id });
    return children.length === 0
      ? [{ kind: "empty", id, depth, label }]
      : [{ kind: "group", id, depth, label }, ...children];
  });
}

export function toActionParamRows(
  value: unknown
): ActionParamRow[] | undefined {
  const params = decodeActionParams(value);
  if (!params) {
    return undefined;
  }
  const rows = toRows({ params, depth: 0, parentId: "" });
  return rows.length > 0 ? rows : undefined;
}
